import React from 'react';
import { useIntl } from 'react-intl';
import { Alert } from 'reactstrap';
import { TypedMemo } from '../common/types';
import { teamErrorMessage } from './teamErrorMessage';
import { acceptMessages, teamFullMessage } from './teamAcceptMessages';
import { readM24Error } from '../music24/utils/parseM24LimitError';
import { workspaceMessages } from '../workspaces/workspaceMessages';
import type { WorkspaceSwitchFailure } from '../workspaces/useSwitchWorkspace';
import { TeamAcceptSignedInAs } from './TeamAcceptSignedInAs';
import type { TeamAcceptInviteState } from './teamAcceptInviteState';

interface TeamAcceptInviteAlertProps {
  state: TeamAcceptInviteState;
  workspaceName: string;
  /** The last failed join, for the `ready` state. */
  acceptError: unknown;
  /** The last failed switch, for the `alreadyMember` state (c5). */
  switchFailure: WorkspaceSwitchFailure | null;
}

/**
 * The one error alert of the join card: c4, c6, the message of a failed join (c7), or a failed
 * "Open {workspace}" (c5, with the copy of the switcher's a4 alert).
 */
const TeamAcceptInviteAlertComponent: React.FC<TeamAcceptInviteAlertProps> = ({
  state,
  workspaceName,
  acceptError,
  switchFailure,
}) => {
  const intl = useIntl();

  switch (state) {
    case 'mismatch':
      return (
        <Alert color="danger">
          {intl.formatMessage({
            id: 'app.sml.team.accept.emailMismatchJoin',
            defaultMessage: 'This invite is for another email address. Sign in with that address to join.',
          })}
          <TeamAcceptSignedInAs className="mt-2 mb-0" />
        </Alert>
      );
    case 'full':
      return <Alert color="danger">{teamFullMessage(intl, workspaceName)}</Alert>;
    case 'ready':
      if (acceptError === null || acceptError === undefined) {
        return null;
      }
      // A join that finds a membership row speaks to the invitee, not with the owner's
      // "This person is already in the team." (only when the preview reload still says ready).
      return (
        <Alert color="danger">
          {readM24Error(acceptError)?.code === 'M24_TEAM_ALREADY_MEMBER'
            ? intl.formatMessage(acceptMessages.alreadyMember, { workspace: workspaceName })
            : teamErrorMessage(intl, acceptError)}
        </Alert>
      );
    case 'alreadyMember':
      return switchFailure === null ? null : (
        <Alert color="danger">
          {intl.formatMessage(
            switchFailure.lostMembership ? workspaceMessages.noLongerMember : workspaceMessages.switchFailed,
            { workspace: workspaceName }
          )}
        </Alert>
      );
    default:
      return null;
  }
};

export const TeamAcceptInviteAlert = TypedMemo(TeamAcceptInviteAlertComponent);
